import { Color } from "@repo/app-types/constants/colors";
import type { Size } from "@repo/ui/helpers/helpers";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import { PillDash } from "@repo/ui/Pills/pillDash";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import type { FC, ReactNode } from "react";
import { SizedContainer } from "../../components/sizedContainer.js";

export interface PercentagePillProps {
  size: Size;
  /**
   * The 0-1 value to render. `undefined` means the number is absent (no data
   * in the period) and renders as a muted `--`; `null` means the metric is
   * genuinely inapplicable and renders as an "N/A" pill.
   */
  value: number | null | undefined;
  isLoading?: boolean;
  computeColor?: (value: number) => PillColor;
  renderPercentage?: (value: number) => ReactNode;
  onClick?: () => void;
  /**
   * Sizes the pill to the widest value it can show — `100%`, or whatever
   * `renderPercentage` makes of it — rather than to the value it does show,
   * so that a column of them lines up.
   */
  sizedToMax?: boolean;
  /**
   * Sizes the pill to this content instead, for a column that holds more than
   * percentages. Takes precedence over `sizedToMax`.
   */
  childrenForSize?: ReactNode;
}

export const PercentagePill: FC<PercentagePillProps> = (props) => {
  const renderLabel = props.renderPercentage ?? renderPercentage;

  const childrenForSize =
    props.childrenForSize ?? (props.sizedToMax ? renderLabel(1) : null);

  const renderPillLabel = (label: ReactNode) => (
    // `tabular-nums`, so that every digit is the width the sizer measured.
    <SizedContainer
      className="text-center tabular-nums"
      childrenForSize={childrenForSize}
    >
      {label}
    </SizedContainer>
  );

  if (props.value == null && props.isLoading) {
    return <PillSkeleton size={props.size} />;
  }

  if (props.value === null) {
    return (
      <Pill
        color={Color.Gray}
        size={props.size}
        label={renderPillLabel("N/A")}
        onClick={props.onClick}
      />
    );
  }

  if (props.value === undefined) {
    return <PillDash size={props.size} />;
  }

  const computeColor = props.computeColor ?? (() => Color.Gray);
  const percentageLabel = renderLabel(props.value);

  return (
    <Pill
      color={computeColor(props.value)}
      size={props.size}
      label={renderPillLabel(percentageLabel)}
      onClick={props.onClick}
    />
  );
};

export function renderPercentage(value: number) {
  return `${(value * 100).toFixed(0)}%`;
}
